import {
	DraftTableRemovePopover,
	SaveDraftTableButtonGroup,
	SaveDraftTableCell
} from "components/NewOrders/SaveDraft/SaveDraftTable/SaveDraftTable.styles.ts";
import { format } from "date-fns";
import { removeConfigWithDupeProductLine } from "components/NewOrders/SaveDraft/SaveDraftTable/saveDraftUtils.ts";
import { UnavailableDataPlaceholderText } from "constants/text.ts";
import { displayDraftDesigner } from "utils/order.ts";
import { convertToTitleCase } from "utils/string.ts";
import { DraftType, OrderType, OrderTypeType } from "types/declarations/enums.ts";
import {
	NewOrderDraftQuoteDownloadButton,
	NewOrderDraftQuoteView,
	NewOrderOpen,
	NewOrderRemoveButton
} from "components/NewOrders/constants.ts";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import { Fragment, useRef, useState } from "react";
import { models } from "types/api/viewModels.ts";
import { useDialog } from "hooks/useDialog.ts";
import useExportDraftQuote from "hooks/useExportDraftQuote.ts";
import { useNavigate } from "react-router";
import { Button, ClickAwayListener, Grow, Paper, Popper } from "@mui/material";
import { LinkButton } from "components/Common/Link";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DeleteIcon from "@mui/icons-material/Delete";

interface SaveDraftTableRowProps {
	draft: models["DraftOrderViewModel"];
	isInternalDesigner?: boolean;
}

const SaveDraftTableRow = ({ draft, isInternalDesigner }: SaveDraftTableRowProps) => {
	const { openDialog } = useDialog();
	const navigate = useNavigate();
	const isQuote = draft.type === DraftType.Quote;
	const isTemplate = draft.type === DraftType.Template;
	const removeOptionAnchorRef = useRef<HTMLButtonElement>(null);

	const { exportFile } = useExportDraftQuote();
	const exportQuote = async (draft: models["DraftOrderViewModel"]) => {
		exportFile(draft);
	};

	const handleOpenDraft = (draft: models["DraftOrderViewModel"]) => {
		const { originalOrderId, draftOrderId, orderType, type } = draft;
		if (!draftOrderId) return;

		if (isInternalDesigner && orderType === OrderType.QUOTE_ORDER && type !== DraftType.Quote) {
			navigate(`/new-quote/build-order/${draftOrderId}`);
			return;
		}

		const routeMap: Partial<Record<OrderTypeType, string>> = {
			[OrderType.ADD_ON]: `/add-ons/build-order/${originalOrderId}/${draftOrderId}`,
			[OrderType.CUSTOM_COLOR]: `/custom-color/build-order/${draftOrderId}`,
			[OrderType.QUOTE_ORDER]: `/new-quote/confirmation/${draftOrderId}`
		};

		const route = (orderType && routeMap[orderType]) ?? `/new-order/build-order/${draftOrderId}`;
		navigate(route);
	};

	const getOpenDraftButtonText = () => {
		if (!isQuote) {
			return NewOrderOpen;
		}

		return NewOrderDraftQuoteView;
	};

	const [openRemoveMenu, setOpenRemoveMenu] = useState(false);
	const handleToggleRemoveMenu = () => {
		setOpenRemoveMenu((prev) => !prev);
	};
	const handleCloseRemoveMenu = (event: Event) => {
		if (removeOptionAnchorRef.current?.contains(event.target as HTMLElement)) {
			return;
		}
		setOpenRemoveMenu(false);
	};

	return (
		<Fragment>
			<SaveDraftTableCell
				data-testid="new-order-save-draft-table-date-content"
				component="th"
				scope="row"
			>
				{draft.updatedAt && format(new Date(String(draft.updatedAt)), "MM/dd/yy")}
			</SaveDraftTableCell>
			<SaveDraftTableCell data-testid="new-order-save-draft-table-draft-name-content">
				{draft.draftName}
			</SaveDraftTableCell>
			<SaveDraftTableCell data-testid="new-order-save-draft-table-account-content">
				{draft.configurations?.find((config) => config.accountNumber) ? (
					removeConfigWithDupeProductLine(draft)
						.configurations?.filter(
							(configuration, index, self) =>
								self.findIndex((item) => item.accountNumber === configuration.accountNumber) === index
						)
						.map((configuration) => (
							<div key={configuration.accountId}>
								{`${configuration.accountNumber} - ${configuration.accountName}`}
							</div>
						))
				) : (
					<div>{UnavailableDataPlaceholderText}</div>
				)}
			</SaveDraftTableCell>
			<SaveDraftTableCell data-testid="new-order-save-draft-table-designer-name-content">
				{displayDraftDesigner(draft?.designerName, draft?.designerNumber)}
			</SaveDraftTableCell>
			<SaveDraftTableCell data-testid="new-order-save-draft-table-brand-content">
				{draft.configurations?.find((config) => config.globals?.productLine?.description) ? (
					removeConfigWithDupeProductLine(draft).configurations?.map((configuration) => (
						<div key={configuration.globals?.productLine?.id}>
							{configuration.globals?.productLine?.description}
						</div>
					))
				) : (
					<div>{UnavailableDataPlaceholderText}</div>
				)}
			</SaveDraftTableCell>
			{isQuote && (
				<SaveDraftTableCell data-testid="new-order-save-draft-table-quote-number-content">
					{draft.draftOrderId ?? UnavailableDataPlaceholderText}
				</SaveDraftTableCell>
			)}
			{!isTemplate && !isQuote && (
				<SaveDraftTableCell data-testid="new-order-save-draft-table-order-type">
					{convertToTitleCase(draft?.orderType)}
				</SaveDraftTableCell>
			)}
			<SaveDraftTableCell fixedWidth={isQuote ? "240px" : "128px"}>
				<SaveDraftTableButtonGroup
					variant="outlined"
					aria-label="Button group with a nested menu"
				>
					<Button
						onClick={() => handleOpenDraft(draft)}
						data-id={`${draft.orderType === OrderType.ADD_ON ? "add-ons" : "new-order"}-save-${isTemplate ? "template" : "draft"}-table-edit-link`}
						data-testid={`new-order-save-${isTemplate ? "template" : "draft"}-table-edit-link`}
					>
						{getOpenDraftButtonText()}
					</Button>
					{isQuote && (
						<Button
							data-testid="new-order-save-quote-table-export-link"
							onClick={async () => await exportQuote(draft)}
						>
							{NewOrderDraftQuoteDownloadButton}
						</Button>
					)}
					<Button
						size="small"
						aria-controls={openRemoveMenu ? "split-button-menu" : undefined}
						aria-expanded={openRemoveMenu ? "true" : undefined}
						aria-label="select merge strategy"
						aria-haspopup="menu"
						data-testid={`new-order-save-${isTemplate ? "template" : "draft"}-table-remove-button`}
						onClick={handleToggleRemoveMenu}
						ref={removeOptionAnchorRef}
					>
						<ArrowDropDownIcon />
					</Button>
				</SaveDraftTableButtonGroup>
				<Popper
					sx={{ zIndex: 1 }}
					open={openRemoveMenu}
					anchorEl={removeOptionAnchorRef.current}
					role={undefined}
					transition
					disablePortal
				>
					{({ TransitionProps }) => (
						<Grow {...TransitionProps}>
							<Paper>
								<ClickAwayListener onClickAway={handleCloseRemoveMenu}>
									<DraftTableRemovePopover
										data-testid="new-order-save-draft-table-remove-popover"
										autoFocusItem
									>
										<DeleteIcon />
										<LinkButton
											variant="remove"
											data-testid={`new-order-save-${isTemplate ? "template" : "draft"}-table-remove-link`}
											onClick={() =>
												openDialog({
													dialogStatus: isTemplate
														? DialogTracking.REMOVE_TEMPLATE
														: DialogTracking.REMOVE_DRAFT,
													dialogId: draft.draftOrderId
												})
											}
										>
											{NewOrderRemoveButton}
										</LinkButton>
									</DraftTableRemovePopover>
								</ClickAwayListener>
							</Paper>
						</Grow>
					)}
				</Popper>
			</SaveDraftTableCell>
		</Fragment>
	);
};

export default SaveDraftTableRow;
